import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * V8.1 · Assessment Express product page (public, no auth).
 * Prices come from /api/catalog/products/single_assessment_express — the
 * frontend never sends an authoritative amount.
 */
export default function AssessmentExpress() {
  const [product, setProduct] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/catalog/products/single_assessment_express")
      .then((r) => setProduct(r.data))
      .catch(() => setProduct(null));
  }, []);

  const buy = async () => {
    if (!email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setBusy(true);
    try {
      const { data: intent } = await api.post("/billing/intents", {
        product_id: "single_assessment_express", email, source: "assessment-express-page",
      });
      // Try to initialize Paystack; if degraded → request access flow
      try {
        const { data: init } = await api.post(
          `/billing/intents/${intent.reference}/initialize`
        );
        if (init.authorization_url) {
          window.location.href = init.authorization_url;
          return;
        }
      } catch (err) {
        const code = err?.response?.data?.detail?.code;
        if (code === "paystack_not_configured" || code === "paystack_channel_error") {
          toast.error("Payments are being finalised. We'll open a manual purchase channel for you.");
          navigate("/request-access?product=assessment_express");
          return;
        }
        throw err;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail?.message || "Could not initialize checkout");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="assessment-express-page" className="max-w-3xl mx-auto px-4 py-12">
      <div className="text-overline text-primary uppercase tracking-widest">Orixa Assessment Express</div>
      <h1 className="text-4xl sm:text-5xl font-serif mt-3">Fast, evidence-backed intelligence on one country & sector.</h1>
      <p className="text-base text-muted-foreground mt-4 leading-relaxed">
        A structured, sourced and reproducible institutional assessment covering
        market attractiveness, regulatory environment, key risks, financial
        feasibility, and an authoritative OIS™ score — with citations and an
        Institutional Confidence banner.
      </p>

      <ul className="mt-8 space-y-2.5 text-sm">
        <li>· 1 country · 1 sector · executive summary</li>
        <li>· Risks · opportunities · deterministic scoring</li>
        <li>· Sources register + inline citations</li>
        <li>· Institutional Confidence rating</li>
        <li>· PDF export (standard institutional layout)</li>
      </ul>

      <div className="mt-4 text-xs text-slate-500 leading-relaxed">
        Delivery time depends on data availability and does not include human
        review. We do not commit to a fixed generation SLA on the Express plan.
      </div>

      {product ? (
        <div data-testid="ax-price-card" className="mt-8 rounded-2xl border border-border p-6 bg-card">
          <div className="flex items-baseline justify-between gap-4 flex-wrap">
            <div>
              <div className="text-overline text-muted-foreground">One-time purchase</div>
              <div className="text-4xl font-serif mt-1">
                {product.currency === "XOF"
                  ? `${product.amount.toLocaleString("fr-FR")} FCFA`
                  : `${(product.amount / 100).toFixed(0)} ${product.currency}`}
              </div>
              <div className="text-xs text-muted-foreground mt-1">Includes 1 assessment credit</div>
            </div>
            <div className="flex-1 min-w-[240px] flex gap-2">
              <input
                data-testid="ax-email"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="flex-1 border border-border rounded-lg px-3 py-2 text-sm"
              />
              <button
                data-testid="ax-buy-btn"
                onClick={buy} disabled={busy}
                className="px-5 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-60"
              >
                {busy ? "Please wait…" : "Buy assessment"}
              </button>
            </div>
          </div>
          {product.is_configured?.paystack === false && product.is_configured?.paddle === false && (
            <div className="mt-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              Payments are being finalised for this environment. Your purchase
              will be captured — a manual channel will open if the automated one
              is unavailable.
            </div>
          )}
        </div>
      ) : (
        <div className="mt-8 text-sm text-muted-foreground">Loading product…</div>
      )}
    </div>
  );
}
